// Team tab of the Schedule Review side panel (UI Phase 1): a browse-only roster of everyone
// in the scheduling scope, sorted by name. Recreates the PR #6802 prototype TeamPanel on top
// of the CardGroup/Card + UserAvatar common components, fed by mock data until GraphQL lands.
// Deferred from the prototype: drag-to-assign, worker → grid highlight, focus bar.
import React, { useMemo, useState } from 'react';
import {
  Button,
  CardGroup,
  Gap,
  Gutter,
  Icon,
  Input,
  Select,
  Text,
} from '@common/components';
import type {
  ScheduleDragStateType,
  ScheduleTeamFteFilterType,
  ScheduleTeamMemberType,
  ScheduleTeamRoleFilterType,
  ScheduleTeamUnitFilterType,
} from '@load-balancing/types';
import { ScheduleReviewTeamTabCard } from './_schedule-review-team-tab-card';
import {
  filterTeamByUnit,
  roleFilterOptions,
  selectTeamDisplay,
} from './_schedule-review-team-tab-service';

type Props = {
  members: ScheduleTeamMemberType[];
  /** Service line the roster belongs to; labels the roster header. The header is hidden when absent. */
  serviceLineName?: string;
  /** All units in the service line — the unit filter options. The filter only renders when >1. */
  serviceLineUnits?: { id: string; name: string }[];
  /** Worker whose eligible (open) shifts are currently ringed in the grid. */
  highlightedWorkerId?: string | null;
  /** Toggle a worker's eligible-shift highlight; `null` clears it. */
  onHighlightWorker?: (workerId: string | null) => void;
  /** Worker whose assigned shifts are currently filled in the grid. */
  assignedHighlightWorkerId?: string | null;
  /** Toggle a worker's assigned-shift highlight; `null` clears it. */
  onHighlightAssigned?: (workerId: string | null) => void;
  /**
   * Worker uuids that have at least one shift assigned in the schedule under review. Only
   * these workers get the "Assigned shifts" toggle — a cross-unit worker with no shifts in
   * this schedule has nothing to highlight (WORK-6529 QA).
   */
  assignedWorkerIds?: string[];
  /**
   * Worker uuids that have at least one eligible open shift in the schedule under review. Only
   * these workers get the "Eligible shifts" toggle — a below-FTE worker with no eligible open
   * shift has nothing to ring, so the toggle would otherwise do nothing (WORK-6551 QA).
   */
  eligibleWorkerIds?: string[];
  /** Open a worker's profile panel (triggered by clicking their name). */
  onWorkerSelect?: (workerId: string) => void;
  /** Drag a worker onto an open grid slot (a new assignment). */
  onWorkerDragStart?: (state: ScheduleDragStateType) => void;
  onWorkerDragEnd?: () => void;
  /**
   * Controlled FTE filter. When provided (with onFteFilterChange), the parent
   * owns the filter — e.g. a "Needs Attention" CTA opening the tab pre-filtered
   * to Above/Below FTE. Falls back to internal state when omitted.
   */
  fteFilter?: ScheduleTeamFteFilterType;
  onFteFilterChange?: (filter: ScheduleTeamFteFilterType) => void;
  /**
   * Controlled worker-id filter: a "Needs Attention" VIEW_IN_TEAM card sets this to the
   * uuids of the staff it flagged, so the roster shows exactly them (composed before the
   * other filters, so they still narrow within the subset). Empty = no filter.
   * onWorkerIdsChange backs the in-tab "Clear" chip that resets it.
   */
  workerIds?: string[];
  onWorkerIdsChange?: (ids: string[]) => void;
};

const FTE_FILTER_OPTIONS: {
  value: ScheduleTeamFteFilterType;
  label: string;
}[] = [
  { value: 'all', label: 'All FTE' },
  { value: 'below', label: 'Under FTE' },
  { value: 'above', label: 'Over FTE' },
];

export function ScheduleReviewTeamTab({
  members,
  serviceLineName,
  serviceLineUnits = [],
  highlightedWorkerId = null,
  onHighlightWorker,
  assignedHighlightWorkerId = null,
  onHighlightAssigned,
  assignedWorkerIds = [],
  eligibleWorkerIds = [],
  onWorkerSelect,
  onWorkerDragStart,
  onWorkerDragEnd,
  fteFilter: controlledFteFilter,
  onFteFilterChange,
  workerIds = [],
  onWorkerIdsChange,
}: Props) {
  // Undefined until the user picks a unit; until then the roster shows the whole service
  // line (WORK-6529: the Team tab defaults to "All units", not the grid's unit).
  const [unitFilter, setUnitFilter] = useState<
    ScheduleTeamUnitFilterType | undefined
  >(undefined);
  const [roleFilter, setRoleFilter] =
    useState<ScheduleTeamRoleFilterType>('all');
  const [internalFteFilter, setInternalFteFilter] =
    useState<ScheduleTeamFteFilterType>('all');
  const [staffFilter, setStaffFilter] = useState('');

  // When the parent-owned worker-id set changes (a "View in Team" card applies one, the
  // Clear chip / a programmatic clear empties it), reset the local fields so they can't
  // silently narrow the incoming set. Resetting during render (React's "adjust state on
  // prop change" pattern) clears them before the roster is computed — no stale frame.
  const [appliedWorkerIds, setAppliedWorkerIds] = useState(workerIds);
  if (workerIds !== appliedWorkerIds) {
    setAppliedWorkerIds(workerIds);
    // 'all', not undefined: undefined falls back to the grid's current unit; a clear should widen.
    setUnitFilter('all');
    setRoleFilter('all');
    setInternalFteFilter('all');
    setStaffFilter('');
  }

  // Controlled when the parent passes fteFilter + onFteFilterChange; otherwise
  // the tab manages it locally.
  const fteFilter = controlledFteFilter ?? internalFteFilter;
  const setFteFilter = onFteFilterChange ?? setInternalFteFilter;

  // The unit filter only appears when the service line spans more than one unit.
  const showUnitFilter = serviceLineUnits.length > 1;
  const effectiveUnitFilter = unitFilter ?? 'all';

  // Workers with a shift in the schedule under review; gates each card's "Assigned shifts" toggle.
  const assignedWorkerIdSet = useMemo(
    () => new Set(assignedWorkerIds),
    [assignedWorkerIds]
  );

  // Workers with an eligible open shift to fill; gates each card's "Eligible shifts" toggle.
  const eligibleWorkerIdSet = useMemo(
    () => new Set(eligibleWorkerIds),
    [eligibleWorkerIds]
  );

  // FTE filtering only applies to home-unit workers (the only ones with FTE). When the unit
  // scope holds none (a sibling unit selected), disable the FTE filter and treat it as "all"
  // so the roster isn't emptied by a stale Below/Over selection (WORK-6529 QA).
  const unitScopedMembers = useMemo(
    () => filterTeamByUnit(members, effectiveUnitFilter),
    [members, effectiveUnitFilter]
  );
  const fteFilterDisabled = !unitScopedMembers.some(
    (member) => member.homeUnitMember
  );
  const effectiveFteFilter: ScheduleTeamFteFilterType = fteFilterDisabled
    ? 'all'
    : fteFilter;

  // Members carry their unit by name (not id), so the filter keys on unit name; the
  // unit id is available on the option data for when member data gains a unit id.
  const unitFilterOptions = useMemo(
    () => [
      { value: 'all', label: 'All units' },
      ...[...serviceLineUnits]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((unit) => ({ value: unit.name, label: unit.name })),
    ],
    [serviceLineUnits]
  );

  // Role options come from the roster itself, so the dropdown lists exactly the roles
  // present (incl. the unit's real abbreviations, across the service line) and a selected
  // value always matches member.role.
  const roleOptions = useMemo(() => roleFilterOptions(members), [members]);

  const displayMembers = selectTeamDisplay(members, {
    workerIds,
    unitFilter: effectiveUnitFilter,
    roleFilter,
    fteFilter: effectiveFteFilter,
    query: staffFilter,
  });

  // Nothing in the roster and no card filtering to flagged staff → no team to show.
  if (members.length === 0 && workerIds.length === 0) {
    return (
      <Gutter asPadding size="md" className="schedule-review-team-tab">
        <EmptyState />
      </Gutter>
    );
  }

  return (
    <Gutter asPadding size="md" className="schedule-review-team-tab">
      <Gap vertical size="sm" fullWidth>
        {serviceLineName && (
          <Text
            size="sm"
            weight="medium"
            color="midnightRegular"
            transform="uppercase"
            letterSpacing="xs"
          >
            {serviceLineName} ∙ {members.length} staff
          </Text>
        )}

        <Input
          prefix={
            <Icon
              icon="search"
              kind="light"
              color="midnightLighter"
              size="sm"
            />
          }
          allowClear
          placeholder="Search staff"
          value={staffFilter}
          onChange={(event) => setStaffFilter(event.target.value)}
        />

        {showUnitFilter && (
          <Select
            showSearch
            size="small"
            value={effectiveUnitFilter}
            onChange={setUnitFilter}
            dropdownMatchSelectWidth={false}
            options={unitFilterOptions}
            style={{ width: '100%' }}
          />
        )}

        <Gap size="sm" fullWidth>
          <Select
            showSearch
            size="small"
            value={roleFilter}
            onChange={setRoleFilter}
            dropdownMatchSelectWidth={false}
            options={roleOptions}
            style={{ flex: 1 }}
          />
          <Select
            showSearch
            size="small"
            value={effectiveFteFilter}
            onChange={setFteFilter}
            disabled={fteFilterDisabled}
            dropdownMatchSelectWidth={false}
            options={FTE_FILTER_OPTIONS}
            style={{ flex: 1 }}
          />
        </Gap>

        {workerIds.length > 0 && (
          <Gap size="xs" align="center">
            <Text size="sm" secondary>
              Filtered from Needs Attention
            </Text>
            <Button
              kind="link"
              size="sm"
              onClick={() => {
                onWorkerIdsChange?.([]);
                setFteFilter('all');
              }}
            >
              Clear
            </Button>
          </Gap>
        )}

        {displayMembers.length === 0 ? (
          <EmptyState staffFilter={staffFilter} />
        ) : (
          <CardGroup variant="joined">
            {displayMembers.map((member) => (
              <ScheduleReviewTeamTabCard
                key={member.id}
                member={member}
                highlightedWorkerId={highlightedWorkerId}
                onHighlightWorker={onHighlightWorker}
                assignedHighlightWorkerId={assignedHighlightWorkerId}
                onHighlightAssigned={onHighlightAssigned}
                hasAssignedShifts={assignedWorkerIdSet.has(member.id)}
                hasEligibleShifts={eligibleWorkerIdSet.has(member.id)}
                onWorkerSelect={onWorkerSelect}
                onWorkerDragStart={onWorkerDragStart}
                onWorkerDragEnd={onWorkerDragEnd}
              />
            ))}
          </CardGroup>
        )}
      </Gap>
    </Gutter>
  );
}

function EmptyState({ staffFilter = '' }: { staffFilter?: string }) {
  const query = staffFilter.trim();
  const primary = query ? `No results for "${query}"` : 'No staff in team';
  const secondary = query
    ? 'Try a different name or clear the search'
    : 'Staff will appear here once the session is loaded';

  return (
    <Gutter asPadding size="md" direction="vertical">
      <Gap vertical size="xs" fullWidth>
        <Text size="lg" weight="medium" color="midnightRegular">
          {primary}
        </Text>
        <Text size="md" secondary>
          {secondary}
        </Text>
      </Gap>
    </Gutter>
  );
}
